/**
 * Curated Google Play reviews, quoted verbatim in their original language (authenticity over
 * translation — decided 2026-09-05). Names shortened to first name + initial; bare handles stay
 * as-is. Selection rule: substantive 4–5★ reviews that name real features.
 *
 * REFRESH: re-pull with the scratchpad google-play-scraper script and re-curate; update the
 * aggregate below from the store listing at the same time.
 */
export type PlayReview = {
    author: string;
    lang: string;
    score: number;
    /** Month the review was posted, ISO yyyy-mm — rendered as a muted freshness hint. */
    month: string;
    text: string;
};

/** The store listing's own aggregate at the last refresh (2026-09-05, country=es). */
export const PLAY_RATING = {score: 4.8, count: 62};

export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.ludoya.app';

export const PLAY_REVIEWS: PlayReview[] = [
    {
        author: 'Stephen K.', lang: 'en', score: 5, month: '2026-01',
        text: 'This is exactly the app I wanted, a quick and easy way to track the board games I have, the ones I’ve ordered and the ones that I want, along with being able to track how often I play each game and also be able to tag games with custom tags. On top of that the developer is very quick to respond on Discord and implement new ideas and features. This app & developer are both fantastic & more people need to download this. You can also import from BGG & BGA.',
    },
    {
        author: 'Dorothy V.', lang: 'en', score: 5, month: '2026-06',
        text: 'I love it. With this app you can keep track of your owned games and played games. You can log your plays, log and calculate scores of all players, and then share your plays if you wish. You can also create score sheets/game kits for games and allow the community to use them. The app updates regularly with an active developer. Fast response to bugs. It can also sync between BGG.',
    },
    {
        author: 'Charlie K.', lang: 'en', score: 5, month: '2024-02',
        text: 'Great looking and runs so smoothly. Love the fact it can sync with board game geek and also enjoy the social homepage seeing people within my region playing games! Invited all my friends to it. Hope more people use it!',
    },
    {
        author: 'Nate T.', lang: 'en', score: 5, month: '2026-02',
        text: 'The best free option.',
    },
    {
        author: 'Roger G.', lang: 'es', score: 5, month: '2025-06',
        text: 'Una aplicación perfecta para los amantes de los juegos de mesa, con opciones tanto de organizar tu colección como de red social especializada (montar mesas de juego, encontrar asociaciones o eventos, conocer gente que comparte la afición y publicar en vivo). Llevo desde sus inicios utilizándola y ha mejorado sustancialmente… con una calidad, mimo y un feedback total por parte del equipo.',
    },
    {
        author: 'Merche G.', lang: 'es', score: 5, month: '2025-02',
        text: 'La mejor para llevar el registro de tus juegos y partidas. Muy intuitiva y buen diseño. Tiene muro donde poder publicar tus partidas con imágenes, con opción de me gusta y mensajes. Posibilidad de unirte a un grupo y organizar partidas. Fácil contactar con el desarrollador y admite sugerencias.',
    },
    {
        author: 'Sara G.', lang: 'es', score: 5, month: '2026-01',
        text: '¡Es una aplicación súper completa! Yo la uso sobre todo para organizar sesiones y partidas, y es muy intuitiva y cómoda.',
    },
    {
        author: 'Carlos U.', lang: 'es', score: 5, month: '2025-08',
        text: 'Genial para jugones, va fluido, sincroniza con BGG, puedes organizar eventos… ¿qué más se puede pedir?',
    },
    {
        author: 'Bernat G.', lang: 'es', score: 5, month: '2025-05',
        text: 'De todas las apps que he probado, ésta destaca por su funcionalidad, diseño intuitivo y actualizaciones frecuentes. El desarrollador realmente se esfuerza por perfeccionarla, ¡y eso se nota! ¡Recomendada 100%!',
    },
    {
        author: 'HidariChan', lang: 'es', score: 5, month: '2023-11',
        text: 'La mejor aplicación que he visto para mantener al día mi biblioteca de juegos de mesa. La hoja de puntuación personalizable que puedes crear para cada juego funciona súper bien y hace que las partidas sean más rápidas. ¡Me encanta!',
    },
    {
        author: 'Mario E.', lang: 'ca', score: 5, month: '2025-07',
        text: 'Amb diferència, la millor aplicació per a gestionar les partides, tindre un registre dels teus jocs de taula, connectar-te amb altres jugadors. I sé que segueixen treballant per a millorar-la encara més.',
    },
    {
        author: 'Ivan S.', lang: 'ca', score: 5, month: '2026-02',
        text: 'La millor aplicació per apuntar partides i compartir l’afició amb la comunitat.',
    },
    {
        author: 'Rafael C.', lang: 'pt', score: 4, month: '2026-01',
        text: 'Great app! As a suggestion, you guys should add filters to the statistics, e.g. the amount of players.',
    },
];

/**
 * The three reviews the home page shows, per site language, by author. Each proves one thing a
 * visitor is deciding on: that it is free, that it syncs with BGG, that it runs a game night or
 * scores a game, that someone answers. Short enough to read at a glance; quoted in full, never
 * trimmed. Languages without a pick of their own get the English one.
 */
const FEATURED: Record<string, string[]> = {
    en: ['Nate T.', 'Charlie K.', 'Carlos U.'],
    es: ['Carlos U.', 'Sara G.', 'HidariChan'],
    ca: ['Ivan S.', 'Mario E.', 'Carlos U.'],
};

export function featuredReviews(locale: string): PlayReview[] {
    const authors = FEATURED[locale.split('-')[0]] ?? FEATURED['en'];
    return authors.flatMap(author => PLAY_REVIEWS.filter(r => r.author === author));
}
